import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageHero";
import { Book3D } from "@/components/Book3D";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ACCENTS = ["bg-ink", "bg-[#5c7355]", "bg-[#6a5a8f]", "bg-[#b45a35]"];

export default function Publications() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("Όλες");
  const [selected, setSelected] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/content/publications").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const categories = ["Όλες", ...new Set(items.map((i) => i.category).filter(Boolean))];
  const filtered = filter === "Όλες" ? items : items.filter((i) => i.category === filter);

  return (
    <div data-testid="publications-page">
      <PageHero
        eyebrow="Εκδόσεις"
        title="Ψηφιακά βιβλία με σκοπό."
        text="Επιστημονικό περιεχόμενο και πρακτικό υλικό για σύγχρονες εκπαιδευτικές και επαγγελματικές ανάγκες."
      />

      <section className="pb-24 sm:pb-32 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 mb-14" data-testid="publication-filters">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c}`}
                onClick={() => setFilter(c)}
                className={`font-mono2 text-xs tracking-[0.15em] uppercase px-4 py-2 rounded-full border transition-colors duration-300 ${
                  filter === c ? "bg-ink text-cream border-ink" : "border-ink/20 text-ink/70 hover:border-terracotta hover:text-terracottaD"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {loaded && filtered.length === 0 && (
            <p className="text-slate2 font-mono2 text-sm tracking-wider" data-testid="publications-empty">
              Δεν υπάρχουν εκδόσεις ακόμα.
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={(i % 4) * 0.07}>
                <button
                  onClick={() => setSelected(p)}
                  data-testid={`publication-item-${i}`}
                  className="text-left w-full group"
                >
                  <div className="flex justify-center py-4">
                    <Book3D small title={p.title} category={p.category} pages={p.pages} accent={ACCENTS[i % ACCENTS.length]} />
                  </div>
                  <p className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-terracottaD mt-6">{p.category}</p>
                  <h3 className="font-display text-xl font-semibold mt-2 leading-snug transition-colors duration-300 group-hover:text-terracottaD">
                    {p.title}
                  </h3>
                  <p className="text-slate2 text-sm mt-2 line-clamp-2">{p.description}</p>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="bg-cream border-ink/10 max-w-lg" data-testid="publication-modal">
          {selected && (
            <>
              <DialogHeader>
                <p className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-terracottaD">{selected.category}</p>
                <DialogTitle className="font-display text-2xl sm:text-3xl font-bold leading-snug text-ink">
                  {selected.title}
                </DialogTitle>
              </DialogHeader>
              <p className="text-slate2 leading-relaxed text-sm sm:text-base">{selected.description}</p>
              <p className="font-mono2 text-xs tracking-[0.15em] uppercase text-ink/50">
                Ψηφιακή μορφή {selected.pages ? `• ${selected.pages} σελίδες` : ""}
              </p>
              <a
                href="mailto:info@noeticalab.gr"
                data-testid="publication-modal-cta"
                className="group inline-flex items-center gap-2 bg-ink text-cream font-medium px-6 py-3 rounded-full transition-colors duration-300 hover:bg-terracotta w-fit"
              >
                Εκδήλωση ενδιαφέροντος
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
